import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";
import LegalFooter from "@/components/LegalFooter";
import { Button } from "@/components/ui/button";
import {
  CloudRain, Snowflake, Layers, MapPin, FileText, ArrowRight, Sprout, Mountain, Building2, Ticket,
} from "lucide-react";

export default function Landing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="landing-page">
      {/* NAV */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <div className="container mx-auto px-4 md:px-8 py-5 flex items-center justify-between">
          <div className="text-white">
            <span className="inline-flex items-center gap-2 font-display tracking-tight text-lg">
              <span className="flex items-center justify-center w-8 h-8 rounded-md bg-white/15 backdrop-blur text-white">
                <Sprout size={18} strokeWidth={2.2} />
              </span>
              <span className="font-bold">BaugrundCheck<span className="text-[#C05A44]">.de</span></span>
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-white/85">
            <a href="#features" className="hover:text-white transition-colors" data-testid="link-features">{t("nav.features")}</a>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageToggle className="bg-white/15 border-white/20 backdrop-blur" />
            {user ? (
              <Button
                data-testid="cta-dashboard"
                onClick={() => navigate("/app")}
                className="bg-white text-[#1A2F24] hover:bg-white/90"
              >
                {t("nav.dashboard")}
              </Button>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline text-sm text-white/85 hover:text-white" data-testid="link-login">
                  {t("nav.login")}
                </Link>
                <Button
                  data-testid="cta-register-top"
                  onClick={() => navigate("/register")}
                  className="hidden md:inline-flex bg-[#C05A44] hover:bg-[#A64B38] text-white"
                >
                  {t("nav.register")}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative min-h-[80vh] flex items-end pb-20 md:pb-32 pt-32 overflow-hidden"
        style={{
          backgroundImage:
            "url('/images/hero-bg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 hero-mask" />
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl text-white">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1 text-xs uppercase tracking-wider">
                <Sprout size={14} /> {t("landing.hero_eyebrow")}
              </div>
              <div
                className="inline-flex items-center gap-2 bg-[#C05A44] border border-[#C05A44] rounded-full px-3 py-1 text-xs uppercase tracking-wider font-medium"
                data-testid="fair-badge"
              >
                <Ticket size={14} /> {t("landing.fair_badge")}
              </div>
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-6">
              {t("landing.hero_title")}
            </h1>
            <p className="text-base md:text-lg text-white/85 max-w-2xl leading-relaxed mb-9">
              {t("landing.hero_subtitle")}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="cta-hero-primary"
                onClick={() => navigate(user ? "/app/query" : "/register")}
                size="lg"
                className="bg-[#C05A44] hover:bg-[#A64B38] text-white px-6 h-12"
              >
                {t("landing.hero_cta_primary")} <ArrowRight size={18} className="ml-1" />
              </Button>
              <Button
                data-testid="cta-hero-secondary"
                onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                variant="outline"
                size="lg"
                className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 hover:text-white h-12 px-6"
              >
                {t("landing.hero_cta_secondary")}
              </Button>
            </div>
            <p className="text-sm text-white/70 max-w-xl mt-6" data-testid="fair-note-hero">
              {t("landing.fair_note")}
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C05A44] font-medium mb-3">
              {t("nav.features")}
            </div>
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl tracking-tighter text-[#1A2F24] mb-4">
              {t("landing.features_title")}
            </h2>
            <p className="text-[#5C6B61] text-base md:text-lg leading-relaxed">
              {t("landing.features_subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E2D9] border border-[#E5E2D9] rounded-xl overflow-hidden">
            {[
              { icon: CloudRain, title: "f1_title", desc: "f1_desc" },
              { icon: Snowflake, title: "f2_title", desc: "f2_desc" },
              { icon: Layers, title: "f3_title", desc: "f3_desc" },
              { icon: Building2Icon, title: "f4_title", desc: "f4_desc" },
              { icon: FileText, title: "f5_title", desc: "f5_desc" },
              { icon: MapPin, title: "f6_title", desc: "f6_desc" },
            ].map((f, i) => (
              <div
                key={i}
                className="bg-[#F9F8F6] p-8 md:p-10 hover:bg-white transition-colors"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-11 h-11 rounded-md bg-[#1A2F24] text-white flex items-center justify-center mb-5">
                  <f.icon size={20} />
                </div>
                <h3 className="font-display font-semibold text-xl text-[#1A2F24] mb-2">
                  {t(`landing.${f.title}`)}
                </h3>
                <p className="text-sm text-[#5C6B61] leading-relaxed">{t(`landing.${f.desc}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#1A2F24] text-white relative overflow-hidden">
        <div className="absolute inset-0 grain-bg opacity-30" />
        <div className="container mx-auto px-4 md:px-8 text-center relative z-10">
          <Mountain size={36} className="mx-auto mb-6 opacity-70" />
          <h2 className="font-display font-bold text-3xl md:text-5xl lg:text-6xl tracking-tighter mb-5 max-w-3xl mx-auto">
            {t("landing.cta_title")}
          </h2>
          <p className="text-base md:text-lg text-white/70 max-w-xl mx-auto mb-9">
            {t("landing.cta_subtitle")}
          </p>
          <Button
            data-testid="cta-bottom"
            onClick={() => navigate(user ? "/app" : "/register")}
            size="lg"
            className="bg-[#C05A44] hover:bg-[#A64B38] text-white h-12 px-7"
          >
            {t("landing.cta_button")} <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <LegalFooter />
    </div>
  );
}

// Import building icon under alias to avoid name clash
function Building2Icon(props) {
  return <Building2 {...props} />;
}
